import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import api from "api";
import styles from "./TrialInsightsPanel.module.css";

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "critical", label: "< 24h" },
  { key: "warning", label: "24–48h" },
  { key: "soon", label: "48h–7d" },
  { key: "d2_pending", label: "E-mail D-2 pendente" },
  { key: "week", label: "Termina em 7d" }
];

function segmentLabel(seg) {
  switch (seg) {
    case "critical":
      return "Crítico";
    case "warning":
      return "Aviso";
    case "soon":
      return "Em breve";
    case "later":
      return "Depois";
    default:
      return seg;
  }
}

function badgeClass(seg) {
  switch (seg) {
    case "critical":
      return styles.bCritical;
    case "warning":
      return styles.bWarning;
    case "soon":
      return styles.bSoon;
    default:
      return styles.bLater;
  }
}

/**
 * @param {object} props
 * @param {string|null} props.token
 * @param {'ribbon'|'panel'} props.variant
 * @param {string|null} [props.syncFilterKey] — forçar filtro (ex.: vindo da ribbon do pai)
 * @param {number} [props.syncNonce] — incrementar para reaplicar o mesmo key
 * @param {(user: object) => void} props.onUserClick
 * @param {(filter: string) => void} [props.onRibbonKpiClick] — navegação para aba Usuários
 */
export default function TrialInsightsPanel({
  token,
  variant = "panel",
  syncFilterKey = null,
  syncNonce = 0,
  onUserClick,
  onRibbonKpiClick
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [ribbonActive, setRibbonActive] = useState(null);

  useEffect(() => {
    if (syncFilterKey != null) {
      setFilter(syncFilterKey);
    }
  }, [syncNonce, syncFilterKey]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get("/admin/trial-insights", {
        headers: { Authorization: `Bearer ${token}` },
        params: { horizon_days: 21 }
      });
      setData(d);
    } catch {
      setErr("Não foi possível carregar insights de trial.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = data?.counts || {};
  const users = data?.users || [];

  const filtered = users.filter(u => {
    if (filter === "all") return true;
    if (filter === "critical") return u.segment === "critical";
    if (filter === "warning") return u.segment === "warning";
    if (filter === "soon") return u.segment === "soon";
    if (filter === "d2_pending") return u.d2_email_pending;
    if (filter === "week") return u.hours_remaining <= 24 * 7;
    return true;
  });

  const onKpiPointer = e => {
    const t = e.currentTarget;
    const r = t.getBoundingClientRect();
    t.style.setProperty("--mx", `${e.clientX - r.left}px`);
    t.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  const ribbonKpi = (key, className, value, label, hint) => (
    <button
      type="button"
      className={`${styles.kpi} ${styles[className] || ""} ${
        ribbonActive === key ? styles.kpiActive : ""
      }`}
      onMouseMove={onKpiPointer}
      onClick={() => {
        setRibbonActive(key);
        if (onRibbonKpiClick) {
          onRibbonKpiClick(key);
          return;
        }
        const map = {
          total: "all",
          critical: "critical",
          warning: "warning",
          d2: "d2_pending",
          week: "week"
        };
        setFilter(map[key] || "all");
      }}
    >
      <div className={styles.kpiValue}>{value}</div>
      <div className={styles.kpiLabel}>{label}</div>
      {hint && <div className={styles.kpiHint}>{hint}</div>}
    </button>
  );

  if (!token) return null;

  if (variant === "ribbon") {
    return (
      <div className={`${styles.panel} ${styles.animateIn}`}>
        <div className={styles.ribbon}>
          {loading && (
            <div className="d-flex align-items-center justify-content-center py-3 w-100">
              <Spinner animation="border" size="sm" style={{ color: "#035184" }} />
            </div>
          )}
          {!loading && err && <div className={styles.error}>{err}</div>}
          {!loading && !err && data && (
            <>
              {ribbonKpi(
                "total",
                "neutral",
                counts.total_active_trials ?? 0,
                "Trials ativos",
                "Clique → aba Usuários"
              )}
              {ribbonKpi(
                "critical",
                "critical",
                counts.expiring_within_24h ?? 0,
                "< 24h",
                "Expira hoje/amanhã"
              )}
              {ribbonKpi(
                "d2",
                "warning",
                counts.d2_email_pending ?? 0,
                "E-mail D-2 pendente",
                "Fila do cron"
              )}
              {ribbonKpi(
                "week",
                "soon",
                counts.expiring_within_7d_total ?? 0,
                "Até 7 dias",
                "Todos nessa janela"
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.panel} ${styles.animateIn}`}>
      <div className={styles.cardShell}>
        <div className={styles.cardHead}>
          <div>
            <h3 className={styles.cardTitle}>
              <i className="fas fa-hourglass-half" />
              Trials — perto do fim
            </h3>
            <p className={styles.sub}>
              Usuários com trial ativo nos próximos 21 dias. Clique na linha para ver timeline.
            </p>
          </div>
          <button
            type="button"
            className={styles.chip}
            onClick={() => load()}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-1" />
            Atualizar
          </button>
        </div>

        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" style={{ color: "#035184" }} />
          </div>
        )}
        {err && <div className={styles.error}>{err}</div>}

        {!loading && !err && data && (
          <>
            <div className={`${styles.ribbon} px-3 pt-3`}>
              {ribbonKpi("total", "neutral", counts.total_active_trials ?? 0, "Ativos", null)}
              {ribbonKpi("critical", "critical", counts.expiring_within_24h ?? 0, "< 24h", null)}
              {ribbonKpi(
                "warning",
                "warning",
                counts.expiring_24h_to_48h ?? 0,
                "24–48h",
                null
              )}
              {ribbonKpi(
                "d2",
                "warning",
                counts.d2_email_pending ?? 0,
                "D-2 e-mail",
                null
              )}
              {ribbonKpi(
                "week",
                "soon",
                counts.expiring_within_7d_total ?? 0,
                "≤ 7 dias",
                null
              )}
            </div>

            <div className="px-3 pb-2">
              <div className={styles.chips}>
                {FILTERS.map(f => (
                  <button
                    key={f.key}
                    type="button"
                    className={`${styles.chip} ${
                      filter === f.key ? styles.chipActive : ""
                    }`}
                    onClick={() => {
                      setFilter(f.key);
                      setRibbonActive(null);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.tableWrap}>
              {filtered.length === 0 ? (
                <div className={styles.empty}>Nenhum usuário neste filtro.</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Usuário</th>
                      <th>E-mail</th>
                      <th>Termina em</th>
                      <th>Horas</th>
                      <th>Faixa</th>
                      <th>D-2 enviado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => (
                      <tr
                        key={u.id}
                        className={styles.clickRow}
                        tabIndex={0}
                        role="button"
                        onKeyDown={e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onUserClick?.({
                              id: u.id,
                              username: u.username,
                              email: u.email
                            });
                          }
                        }}
                        onClick={() =>
                          onUserClick?.({
                            id: u.id,
                            username: u.username,
                            email: u.email
                          })
                        }
                      >
                        <td>
                          <strong>{u.username || `#${u.id}`}</strong>
                        </td>
                        <td className="text-muted small">{u.email}</td>
                        <td className="small">
                          {u.trial_ends_at
                            ? new Date(u.trial_ends_at).toLocaleString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                              })
                            : "—"}
                        </td>
                        <td>{Math.round(u.hours_remaining)}h</td>
                        <td>
                          <span className={`${styles.badge} ${badgeClass(u.segment)}`}>
                            {segmentLabel(u.segment)}
                          </span>
                        </td>
                        <td>
                          {u.trial_warning_email_sent_at ? (
                            <span className="text-success small">
                              <i className="fas fa-check me-1" />
                              Sim
                            </span>
                          ) : u.d2_email_pending ? (
                            <span className="text-warning small">
                              <i className="fas fa-envelope me-1" />
                              Pendente
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
